import { supabase } from '../lib/supabase';
import { sendMessageToParent } from '../utils/embedMessaging';
import { uploadPdfToStorage } from './storageService';

export interface DocumentUploadPayload {
  documentId: string;
  documentType: string;
  documentTitle: string;
  pdfBlob: Blob;
  metadata: {
    propertyAddress?: string;
    generatedAt: string;
    tier: string;
    formData: Record<string, any>;
  };
}

export interface UploadResponse {
  success: boolean;
  parentDocumentId?: string;
  pdfSignedUrl?: string;
  error?: string;
}

const UPLOAD_TIMEOUT = 15000;
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 2000;

export async function uploadDocumentToParent(
  payload: DocumentUploadPayload,
  attemptNumber: number = 1
): Promise<UploadResponse> {
  try {
    await supabase
      .from('document_generations')
      .update({
        upload_attempts: attemptNumber,
        sync_status: 'uploading',
      })
      .eq('id', payload.documentId);

    const { path: pdfStoragePath, signedUrl: pdfUrl } = await uploadPdfToStorage(
      payload.documentId,
      payload.pdfBlob
    );

    await supabase
      .from('document_generations')
      .update({ pdf_storage_path: pdfStoragePath })
      .eq('id', payload.documentId);

    const response = await sendUploadRequestWithTimeout(payload, pdfUrl);

    if (response.success && response.parentDocumentId) {
      await supabase
        .from('document_generations')
        .update({
          parent_document_id: response.parentDocumentId,
          uploaded_at: new Date().toISOString(),
          sync_status: 'uploaded',
          last_upload_error: null,
        })
        .eq('id', payload.documentId);

      sendMessageToParent({
        type: 'documentUploadComplete',
        data: {
          documentId: payload.documentId,
          parentDocumentId: response.parentDocumentId,
          documentType: payload.documentType,
        },
      });

      return { ...response, pdfSignedUrl: pdfUrl };
    } else {
      throw new Error(response.error || 'Upload failed without error message');
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';

    await supabase
      .from('document_generations')
      .update({
        last_upload_error: errorMessage,
        sync_status: attemptNumber >= MAX_RETRY_ATTEMPTS ? 'upload_failed' : 'uploading',
      })
      .eq('id', payload.documentId);

    if (attemptNumber < MAX_RETRY_ATTEMPTS) {
      const delay = RETRY_DELAY_MS * Math.pow(2, attemptNumber - 1);
      await new Promise((resolve) => setTimeout(resolve, delay));
      return uploadDocumentToParent(payload, attemptNumber + 1);
    }

    return { success: false, error: errorMessage };
  }
}

function sendUploadRequestWithTimeout(
  payload: DocumentUploadPayload,
  pdfUrl: string
): Promise<UploadResponse> {
  return new Promise((resolve, reject) => {
    const requestId = generateRequestId();
    let timeoutId: ReturnType<typeof setTimeout>;

    const messageHandler = (event: MessageEvent) => {
      const msg = event.data;
      const payload = msg?.data;
      if (
        msg?.type === 'documentUploadResponse' &&
        payload?.requestId === requestId
      ) {
        clearTimeout(timeoutId);
        window.removeEventListener('message', messageHandler);
        resolve({
          success: payload.success,
          parentDocumentId: payload.parentDocumentId,
          error: payload.error,
        });
      }
    };

    window.addEventListener('message', messageHandler);

    timeoutId = setTimeout(() => {
      window.removeEventListener('message', messageHandler);
      reject(new Error('Parent app did not respond within timeout period'));
    }, UPLOAD_TIMEOUT);

    sendMessageToParent({
      type: 'documentUploadRequest',
      data: {
        requestId,
        documentId: payload.documentId,
        documentType: payload.documentType,
        documentTitle: payload.documentTitle,
        pdfUrl,
        metadata: payload.metadata,
      },
    });
  });
}

function generateRequestId(): string {
  return `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export async function fallbackToLocalStorage(
  documentId: string,
  errorMessage: string
): Promise<void> {
  await supabase
    .from('document_generations')
    .update({
      sync_status: 'local_only',
      last_upload_error: `Parent upload failed: ${errorMessage}. Saved locally instead.`,
    })
    .eq('id', documentId);

  sendMessageToParent({
    type: 'documentStoredLocally',
    data: { documentId, reason: errorMessage },
  });
}
